import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

// Read model for the Daily Update List tab: manually flagged variants (the
// star toggle in Master Products), sorted by inquiryCount desc, with deal
// aggregates for staleness.
const endpoint = 'ums/v1/cms/exclusive-deals/top-products';
export const getTopProductsEndpointIdentifier = endpoint;

export interface TopProductRow {
  masterProductId: number;
  familyId: number;
  universalName: string;
  catalogCategoryId: number;
  brand: string | null;
  origin: string | null;
  state: string | null;
  spec: string | null;
  baseUnit: string;
  netContent: number | null;
  itemUnit: string | null;
  packUnit: string | null;
  packSize: number | null;
  inquiryCount: number;
  supplierCount: number;
  bestPrice: number | null;
  bestPriceUnit: string | null;
  lastPriceUpdateAt: string | null;
}

interface ResponseData {
  data: TopProductRow[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetTopProductsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async () => {
    try {
      const { data } = await http.get(endpoint);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper([endpoint], queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
